import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['closeable', 'toggleable']

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener('click', this.handleOutsideClick)
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('resize', this.handleResize)
  }

  toggleTarget(event) {
    const targets = event.currentTarget.dataset.targets?.split(',') || []
    const classes = event.currentTarget.dataset.classes?.split(',') || ['hidden']
    
    targets.forEach((target, index) => {
      const className = classes[index] || classes[0] || 'hidden'
      const elements = target.startsWith('.')
        ? document.querySelectorAll(target)
        : document.querySelectorAll(`#${target}`)

      elements.forEach(element => {
        // Special handling for disabled class and data-enabled attribute
        if (className === 'disabled') {
          if (event.currentTarget.checked) {
            element.classList.remove('disabled', 'cursor-not-allowed', '!bg-gray-200', '!text-gray-400', 'hover:!bg-gray-200')
            element.classList.add('cursor-pointer')
            element.dataset.enabled = 'true'
          } else {
            element.classList.add('disabled', 'cursor-not-allowed', '!bg-gray-200', '!text-gray-400', 'hover:!bg-gray-200')
            element.classList.remove('cursor-pointer')
            element.dataset.enabled = 'false'
          }
        } else {
          className.split(' ').forEach(cls => element.classList.toggle(cls))
        }
      })
    })
  }

  // Like toggleTarget, but ensures only one closeable is open at a time within
  // the controller's scope — used by nav dropdowns where opening one should
  // close the others.
  toggleExclusive(event) {
    const targets = event.currentTarget.dataset.targets?.split(',') || []
    const firstTarget = document.getElementById(targets[0])
    const targetWasOpen = firstTarget && !firstTarget.classList.contains('hidden')

    this.hideAll()
    if (!targetWasOpen) this.toggleTarget(event)
  }

  preventDisabled(event) {
    if (event.target.dataset.enabled != 'true') {
      event.preventDefault()
      return false
    }
  }

  select(event) {
    const groupName = event.currentTarget.dataset.selectGroup;
    const selectedValue = event.currentTarget.dataset.selectValue;

    if (!groupName || !selectedValue) return;

    // Update display elements (e.g., stacked images or similar visuals)
    const displayElements = document.querySelectorAll(`img[data-select-group="${groupName}"]`);
    displayElements.forEach(img => {
      const elementValue = img.dataset.selectValue;
      const isSelected = elementValue === selectedValue;

      img.dataset.selectState = isSelected ? 'selected' : 'unselected';

      if (isSelected) {
        img.classList.remove('opacity-0');
      } else {
        img.classList.add('opacity-0');
      }
    });

    // Update state elements (e.g., for radio-like selections with separate selected/unselected states)
    const stateElements = document.querySelectorAll(`[data-select-group="${groupName}"]:not(img)`);
    stateElements.forEach(el => {
      const elementValue = el.dataset.selectValue;
      const elementState = el.dataset.selectState;
      const isSelected = elementValue === selectedValue;

      if ((isSelected && elementState === 'selected') || (!isSelected && elementState === 'unselected')) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  toggle() {
    this.toggleAll()
  }

  toggleAll() {
    // Toggle all toggleable targets - useful for showing skeleton while loading
    if (this.hasToggleableTarget) {
      this.toggleableTargets.forEach(element => {
        element.classList.toggle('hidden')
      })
    }
  }

  hideAll() {
    this.closeableTargets.forEach(element => {
      const closeClass = element.dataset.closeClass || 'hidden'
      const action = element.dataset.closeAction || 'add'

      closeClass.split(' ').forEach(cls => {
        if (action === 'add') {
          element.classList.add(cls)
        } else if (action === 'remove') {
          element.classList.remove(cls)
        }
      })
    })
  }

  handleOutsideClick(event) {
    // Check if click was on a toggle button (either toggleTarget or toggleExclusive)
    const clickedToggleButton = event.target.closest('[data-action*="toggle#toggleTarget"], [data-action*="toggle#toggleExclusive"]')

    // A click inside a dropdown this controller owns is not an outside click. Without
    // this, anything interactive in a dropdown — a stepper, a number field — closed the
    // dropdown the moment you used it. Named menus stay listed: they are not always
    // registered as closeable targets on the controller handling the click.
    const clickedInsideOwnCloseable = this.hasCloseableTarget &&
      this.closeableTargets.some(element => element.contains(event.target))

    const mobileMenu = document.getElementById('mobileMenu')
    const userDropdown = document.getElementById('userDropdownMenu')
    const clickedInsideMenu = clickedInsideOwnCloseable ||
                             (mobileMenu && mobileMenu.contains(event.target)) ||
                             (userDropdown && userDropdown.contains(event.target))

    // If clicked outside and not on a toggle button, hide all closeable items (not toggleable items)
    if (!clickedToggleButton && !clickedInsideMenu) {
      this.hideAll()
    }
  }

  handleResize() {
    // Hide everything on resize
    if (window.innerWidth >= 768) {
      this.hideAll()
    }
  }

  selectRadioGroup(event) {
    const groupName = event.currentTarget.dataset.radioGroup
    const selectedValue = event.currentTarget.dataset.radioValue

    // Hide all selected items in this group, show all unselected
    document.querySelectorAll(`[data-radio-group="${groupName}"][data-radio-state="selected"]`).forEach(el => el.classList.add('hidden'))
    document.querySelectorAll(`[data-radio-group="${groupName}"][data-radio-state="unselected"]`).forEach(el => el.classList.remove('hidden'))

    // Show ALL selected versions for the clicked value (not just first match)
    document.querySelectorAll(`[data-radio-group="${groupName}"][data-radio-state="selected"][data-radio-value="${selectedValue}"]`).forEach(el => el.classList.remove('hidden'))
    document.querySelectorAll(`[data-radio-group="${groupName}"][data-radio-state="unselected"][data-radio-value="${selectedValue}"]`).forEach(el => el.classList.add('hidden'))
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  selectRadio(event) {
    // Handle conditional showing based on radio selection
    const value = event.currentTarget.dataset.value
    const conditionalSections = this.element.querySelectorAll('[data-show-when]')

    conditionalSections.forEach(section => {
      const showWhen = section.dataset.showWhen
      if (showWhen === value) {
        section.classList.remove('hidden')
      } else {
        section.classList.add('hidden')
      }
    })
  }
}
